import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileLines,
  faTriangleExclamation,
} from '@fortawesome/pro-light-svg-icons';
import { useClient } from 'shared/apollo';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactDropdown from 'shared/design-system/components/TrueImpactDropdown';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactConfirmationDialog from 'shared/design-system/components/TrueImpactConfirmationDialog';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import SectionAccordion from './SectionAccordion';
import EditQuestionModal from './EditQuestionModal';
import {
  bulkSetProgramQuestionFlags,
  fetchAdminProgramQuestionsOverview,
  updateNpoReportQuestion,
} from './api';
import {
  AdminProgramQuestion,
  AdminProgramQuestionsOverview,
  UpdateNpoReportQuestionInput,
} from './types';

interface ReportingOption {
  id: string;
  label: string;
}

const REPORTING_OPTIONS: ReportingOption[] = [
  { id: 'all', label: 'All reporting types' },
  { id: 'full', label: 'Full' },
  { id: 'light', label: 'Light' },
  // { id: 'super_light', label: 'Super light' },
];

const PageWrapper = styled.div`
  width: 100%;
  max-width: 1024px;
  margin: 0 auto;
  padding: ${SPACERS.large};
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACERS.small};
  margin-bottom: ${SPACERS.medium};
  .icon {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: ${COLORS.ctaPrimaryBackground};
    color: ${COLORS.ctaPrimary};
  }
  .heading {
    margin: 0;
  }
  .subheading {
    margin: 2px 0 0;
    color: ${COLORS.grayDark};
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACERS.medium};
  margin-bottom: ${SPACERS.medium};
  .filter {
    min-width: 260px;
  }
  .spacer {
    flex: 1;
  }
  .summary {
    color: ${COLORS.grayDark};
  }
`;

const ErrorBanner = styled.div`
  padding: ${SPACERS.small} ${SPACERS.medium};
  background: ${COLORS.criticalBackground};
  color: ${COLORS.critical};
  border-radius: 8px;
  margin-bottom: ${SPACERS.small};
`;

const NotAuthorized = styled.div`
  padding: ${SPACERS.large};
  text-align: center;
`;

const ProgramProfilingAdmin: React.FC = () => {
  const client = useClient();
  const { accountId } = useUserActions();
  const isSuperAdmin = accountId === '1';

  const [
    overview,
    setOverview,
  ] = useState<AdminProgramQuestionsOverview | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const [reportingFilter, setReportingFilter] = useState<ReportingOption>(
    REPORTING_OPTIONS[0],
  );

  // Pending toggle map — questionId -> next isProgramQuestion.
  const [pending, setPending] = useState<Record<number, boolean>>({});
  const [saving, setSaving] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

  // Edit modal state.
  const [editing, setEditing] = useState<AdminProgramQuestion | null>(null);
  const [editSaving, setEditSaving] = useState<boolean>(false);
  const [editError, setEditError] = useState<string | undefined>(undefined);

  const load = useCallback(async () => {
    if (!client || !isSuperAdmin) return;
    setLoading(true);
    setErrorText(undefined);
    try {
      const res = await fetchAdminProgramQuestionsOverview(client, {
        reportingType: reportingFilter.id === 'all' ? null : reportingFilter.id,
        includeDisabled: false,
      });
      setOverview(res.data?.adminProgramQuestionsOverview ?? null);
      setPending({});
    } catch (err) {
      setErrorText(
        err instanceof Error ? err.message : 'Failed to load questions.',
      );
    } finally {
      setLoading(false);
    }
  }, [client, isSuperAdmin, reportingFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // --- Flag toggling -------------------------------------------------------

  const handleToggleFlag = useCallback(
    (question: AdminProgramQuestion, next: boolean) => {
      setPending(prev => {
        const copy = { ...prev };
        if (next === question.isProgramQuestion) {
          delete copy[question.questionId];
        } else {
          copy[question.questionId] = next;
        }
        return copy;
      });
    },
    [],
  );

  // Compute the flat item list + whether any pending toggle would orphan
  // existing program answers (unflag with programAnswersCount > 0).
  const pendingItems = useMemo(
    () =>
      Object.entries(pending).map(([id, isProgramQuestion]) => ({
        questionId: Number(id),
        isProgramQuestion,
      })),
    [pending],
  );

  const orphanCandidates = useMemo(() => {
    if (!overview) return [];
    const flatQuestions = overview.sections.flatMap(s => s.questions);
    return pendingItems
      .filter(item => item.isProgramQuestion === false)
      .map(item => flatQuestions.find(q => q.questionId === item.questionId))
      .filter(
        (q): q is AdminProgramQuestion =>
          !!q && q.isProgramQuestion && q.programAnswersCount > 0,
      );
  }, [overview, pendingItems]);

  const commitPending = useCallback(async () => {
    if (!client || pendingItems.length === 0) return;
    setSaving(true);
    setErrorText(undefined);
    try {
      const res = await bulkSetProgramQuestionFlags(client, pendingItems);
      const success = res.data?.adminBulkSetProgramQuestionFlags.success;
      if (!success) {
        setErrorText('Failed to save changes.');
        return;
      }
      // Patch the overview cache in place using the returned questions.
      const returned =
        res.data?.adminBulkSetProgramQuestionFlags.questions ?? [];
      const byId = new Map(returned.map(q => [q.questionId, q]));
      setOverview(prev =>
        prev
          ? {
              ...prev,
              programFlagged:
                prev.programFlagged +
                pendingItems.reduce(
                  (acc, item) => acc + (item.isProgramQuestion ? 1 : -1),
                  0,
                ),
              sections: prev.sections.map(s => {
                let programFlagged = s.programFlagged;
                const nextQuestions = s.questions.map(q => {
                  const updated = byId.get(q.questionId);
                  if (!updated) return q;
                  if (updated.isProgramQuestion && !q.isProgramQuestion) {
                    programFlagged += 1;
                  } else if (
                    !updated.isProgramQuestion &&
                    q.isProgramQuestion
                  ) {
                    programFlagged -= 1;
                  }
                  return { ...q, ...updated };
                });
                return { ...s, programFlagged, questions: nextQuestions };
              }),
            }
          : prev,
      );
      setPending({});
    } catch (err) {
      setErrorText(
        err instanceof Error ? err.message : 'Failed to save changes.',
      );
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  }, [client, pendingItems]);

  const handleSaveClick = useCallback(() => {
    if (orphanCandidates.length > 0) {
      setConfirmOpen(true);
      return;
    }
    commitPending();
  }, [orphanCandidates.length, commitPending]);

  // --- Edit-question flow --------------------------------------------------

  const handleEditQuestion = useCallback((q: AdminProgramQuestion) => {
    setEditError(undefined);
    setEditing(q);
  }, []);

  const handleEditClose = useCallback(() => {
    if (editSaving) return;
    setEditing(null);
    setEditError(undefined);
  }, [editSaving]);

  const handleEditSave = useCallback(
    async (patch: UpdateNpoReportQuestionInput) => {
      if (!client || !editing) return;
      setEditSaving(true);
      setEditError(undefined);
      try {
        const res = await updateNpoReportQuestion(
          client,
          editing.questionId,
          patch,
        );
        if (!res.data?.updateNpoReportQuestion.success) {
          setEditError('Failed to update question.');
          return;
        }
        // Refetch overview so any renamed section / reordered field
        // reflects immediately. Cheap and rare.
        setEditing(null);
        await load();
      } catch (err) {
        setEditError(
          err instanceof Error ? err.message : 'Failed to update question.',
        );
      } finally {
        setEditSaving(false);
      }
    },
    [client, editing, load],
  );

  // --- Render --------------------------------------------------------------

  if (!isSuperAdmin) {
    return (
      <PageWrapper>
        <NotAuthorized>
          <TrueImpactText variant="heading-subheading">
            Not authorized
          </TrueImpactText>
          <TrueImpactText variant="body">
            Program Profiles administration is only available to superadmin
            users.
          </TrueImpactText>
        </NotAuthorized>
      </PageWrapper>
    );
  }

  const dirtyCount = pendingItems.length;

  return (
    <PageWrapper>
      <Header>
        <div className="icon">
          <FontAwesomeIcon icon={faFileLines} size="lg" />
        </div>
        <div>
          <TrueImpactText variant="heading-page" className="heading">
            Program Profiles
          </TrueImpactText>
          <TrueImpactText variant="body-small" className="subheading">
            Manage which report questions are program-level, and edit question
            metadata.
          </TrueImpactText>
        </div>
      </Header>

      <Toolbar>
        <div className="filter">
          <TrueImpactDropdown
            value={reportingFilter}
            options={REPORTING_OPTIONS}
            getId={o => o.id}
            getLabel={o => o.label}
            native={true}
            onChange={setReportingFilter}
            onClear={undefined}
            getItemDisability={undefined}
            name="Reporting type"
          />
        </div>
        <div className="spacer" />
        {overview && (
          <TrueImpactText variant="body-small" className="summary">
            {overview.programFlagged}/{overview.total} flagged as program-level
          </TrueImpactText>
        )}
        <TrueImpactButton
          disabled={dirtyCount === 0 || saving}
          isLoading={saving}
          onClick={handleSaveClick}
        >
          {dirtyCount === 0 ? 'Save changes' : `Save changes (${dirtyCount})`}
        </TrueImpactButton>
      </Toolbar>

      {errorText && <ErrorBanner>{errorText}</ErrorBanner>}

      {loading || !overview ? (
        <TrueImpactLoading />
      ) : (
        overview.sections.map(section => (
          <SectionAccordion
            key={section.section || 'uncategorized'}
            section={section}
            flagState={pending}
            disabled={saving}
            onToggleFlag={handleToggleFlag}
            onEditQuestion={handleEditQuestion}
          />
        ))
      )}

      <EditQuestionModal
        open={editing != null}
        question={editing}
        isSaving={editSaving}
        errorText={editError}
        onSave={handleEditSave}
        onClose={handleEditClose}
      />

      <TrueImpactConfirmationDialog
        open={confirmOpen}
        icon={faTriangleExclamation}
        iconColor="warningDark"
        handleClose={() => (saving ? undefined : setConfirmOpen(false))}
        next={{
          text: 'Save anyway',
          disabled: saving,
          isLoading: saving,
          onClick: commitPending,
        }}
        previous={{
          text: 'Cancel',
          disabled: saving,
          isLoading: false,
          onClick: () => setConfirmOpen(false),
        }}
      >
        <TrueImpactText variant="heading-subheading" align="center">
          Existing program answers will be orphaned
        </TrueImpactText>
        <TrueImpactText variant="body" align="center">
          You are about to un-flag {orphanCandidates.length} question
          {orphanCandidates.length === 1 ? '' : 's'} that already have
          program-level answers. The answers stay in the database but stop being
          copied to new reports via <code>copyProgramAnswersToReport</code>.
          This can be undone by re-flagging the question later.
        </TrueImpactText>
      </TrueImpactConfirmationDialog>
    </PageWrapper>
  );
};

export default ProgramProfilingAdmin;
